import { useEmployee, useLegalEntities } from '@omnipresentgroup/domains';
import {
  type Employment,
  EmploymentStatus,
  EmploymentStatusMap,
  EmploymentType,
} from '@omnipresentgroup/types';
import type { UiSchema } from '@rjsf/utils';
import { getCountryCode } from 'countries-list';
import type { JSONSchema7, JSONSchema7Definition } from 'json-schema';
import { useEffect, useMemo, useState } from 'react';

import {
  EMPLOYEE_EMPLOYMENT_TYPE_MAPPING,
  EMPLOYMENT_LEGAL_ENTITY_TYPES_MAPPING,
} from '../constants';
import { type EmploymentFormFields, FormAction } from '../types';

const TYPE_OPTIONS = Object.values(EmploymentType)
  .map((type) => ({
    const: type,
    title: type,
  }))
  .sort((a, b) => a.title.localeCompare(b.title));

export const useEmploymentFormSchema = ({
  employeeId,
  formAction,
  employment,
  formValues,
}: {
  employeeId: string;
  formAction: FormAction;
  employment?: Employment;
  formValues?: EmploymentFormFields;
}) => {
  const [schema, setSchema] = useState<JSONSchema7>({});
  const [uiSchema, setUiSchema] = useState<UiSchema>({});

  const { data: employee, isLoading: isLoadingEmployee } = useEmployee({
    id: employeeId,
    enabled: !!employeeId,
  });

  const employeeCountryCode = useMemo(
    () =>
      (employee?.country.name && getCountryCode(employee.country.name)) ||
      undefined,
    [employee?.country.name],
  );

  const { data: legalEntities, isLoading: isLoadingLegalEntities } =
    useLegalEntities({
      isActive: true,
      countryCode: employeeCountryCode,
      types:
        formValues?.type &&
        EMPLOYMENT_LEGAL_ENTITY_TYPES_MAPPING[formValues.type],
      enabled: Boolean(employeeCountryCode),
    });

  const legalEntityOptions = useMemo(
    () =>
      legalEntities
        ?.map((item) => ({
          const: item.id,
          title: item.name,
        }))
        .sort((a, b) => a.title.localeCompare(b.title)) || [],
    [legalEntities],
  );

  const statusOptions = useMemo(() => {
    const availableStatuses: EmploymentStatus[] = employment?.status
      ? [employment.status]
      : [];
    switch (employment?.status) {
      case EmploymentStatus.CREATED: {
        availableStatuses.push(EmploymentStatus.INACTIVE);
        break;
      }
      case EmploymentStatus.ONBOARDED: {
        availableStatuses.push(EmploymentStatus.INACTIVE);
        break;
      }
      case EmploymentStatus.INACTIVE: {
        availableStatuses.push(EmploymentStatus.CREATED);
        break;
      }
    }
    return availableStatuses
      .map((status) => ({
        const: status,
        title: EmploymentStatusMap[status],
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [employment?.status]);

  const canEdit = useMemo(() => {
    return (
      employee?.currentEmploymentId !== employment?.id ||
      employment?.status === EmploymentStatus.CREATED
    );
  }, [employee?.currentEmploymentId, employment?.id, employment?.status]);

  useEffect(() => {
    setSchema({
      title: '',
      type: 'object',
      required: [
        'type',
        'startDate',
        'legalEntityId',
        ...(formAction === FormAction.UPDATE ? ['status'] : []),
      ],
      properties: {
        type: {
          type: 'string',
          title: 'Employment type',
          oneOf: TYPE_OPTIONS,
          default:
            (formAction === FormAction.ADD &&
              employee?.employeeType &&
              EMPLOYEE_EMPLOYMENT_TYPE_MAPPING[employee.employeeType]) ||
            undefined,
        },
        legalEntityId: {
          type: 'string',
          title: 'Employer',
          ...(legalEntityOptions.length > 0
            ? { oneOf: legalEntityOptions }
            : {}),
        },
        startDate: {
          type: 'string',
          title: 'Start date',
          format: 'date',
        },
        terminationDate: {
          type: ['string', 'null'],
          title: 'Termination date',
          format: 'date',
        },
        firstPayrollDate: {
          type: ['string', 'null'],
          title: 'First payroll date',
          format: 'date',
        },
        lastPayrollDate: {
          type: ['string', 'null'],
          title: 'Last payroll date',
          format: 'date',
        },
        ...(formAction === FormAction.UPDATE
          ? {
              status: {
                type: 'string',
                title: 'Status',
                ...(statusOptions.length > 0 ? { oneOf: statusOptions } : {}),
              },
            }
          : {}),
      } satisfies Partial<
        Record<keyof EmploymentFormFields, JSONSchema7Definition>
      >,
    });
    setUiSchema({
      'ui:submitButtonOptions': {
        norender: true,
      },
      'ui:order': [
        'type',
        'legalEntityId',
        'startDate',
        'terminationDate',
        'firstPayrollDate',
        'lastPayrollDate',
        'status',
      ],
      legalEntityId: {
        'ui:options': {
          placeholder:
            legalEntityOptions.length === 0
              ? 'No options available'
              : 'Select an employer',
          readonly:
            legalEntityOptions.length === 0 ||
            (formAction === FormAction.UPDATE && !canEdit),
          isSearchable: true,
        },
      },
      type: {
        'ui:options': {
          placeholder: 'Select a type',
          readonly: formAction === FormAction.UPDATE && !canEdit,
        },
      },
      startDate: {
        'ui:options': {
          readonly: formAction === FormAction.UPDATE && !canEdit,
        },
      },
      terminationDate: {
        'ui:options': {
          minDate: formValues?.startDate,
          emptyValue: null,
        },
      },
      firstPayrollDate: {
        'ui:options': {
          readonly: formAction === FormAction.UPDATE && !canEdit,
          emptyValue: null,
        },
      },
      lastPayrollDate: {
        'ui:options': {
          minDate: formValues?.firstPayrollDate,
          emptyValue: null,
        },
      },
      status: {
        'ui:options': {
          placeholder: 'Select a status',
          readonly: formAction === FormAction.UPDATE && !canEdit,
        },
      },
    });
  }, [
    formAction,
    formValues?.firstPayrollDate,
    formValues?.startDate,
    legalEntityOptions,
    canEdit,
    legalEntities,
    statusOptions,
    employee?.employeeType,
  ]);

  return {
    schema,
    uiSchema,
    isLoading: isLoadingEmployee || isLoadingLegalEntities,
  };
};
